import React, { useReducer, useCallback, useRef, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography, SxProps, Theme, Menu, MenuItem } from '@mui/material';
import Draggable, { DraggableEvent, DraggableData } from 'react-draggable';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PushPinIcon from '@mui/icons-material/PushPin';
import { produce } from 'immer';
import debounce from 'lodash.debounce';

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
type PinPosition = 'left' | 'right' | 'top' | 'bottom' | null;

interface WindowSize {
  width: number;
  height: number;
  unit: 'px' | '%';
}

interface WindowSizeInput {
  width: string | number;
  height: string | number;
  unit?: 'px' | '%';
}

interface WindowPosition {
  x: number;
  y: number;
}

interface WindowConstraints {
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
  snapToGrid: number;
}

interface LegacySize {
  width: string;
  height: string;
}

interface WindowState {
  position: WindowPosition;
  size: WindowSize;
  isMinimized: boolean;
  pinPosition: PinPosition;
  zIndex: number;
  previousSize?: WindowSize;
  previousPosition?: WindowPosition;
  isDragging: boolean;
  isResizing: boolean;
}

interface DraggableWindowProps {
  title: string;
  children: React.ReactNode;
  initialPosition?: WindowPosition;
  initialSize?: WindowSizeInput;
  // Legacy props (for backward compatibility)
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  dragEnabled?: boolean;
  onPositionChange?: (position: WindowPosition) => void;
  onSizeChange?: (size: LegacySize) => void;
  // New props
  constraints?: Partial<WindowConstraints>;
  features?: {
    minimizable?: boolean;
    pinnable?: boolean;
    resizable?: boolean;
    draggable?: boolean;
    snapToEdges?: boolean;
  };
  zIndex?: number;
  darkMode?: boolean;
  className?: string;
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
  headerControls?: React.ReactNode;
  onStateChange?: (state: WindowState) => void;
  footer?: React.ReactNode;
}

type WindowAction =
  | { type: 'MOVE'; payload: WindowPosition }
  | { type: 'START_DRAG' }
  | { type: 'END_DRAG' }
  | { type: 'RESIZE'; payload: Partial<WindowSize> }
  | { type: 'START_RESIZE' }
  | { type: 'END_RESIZE' }
  | { type: 'TOGGLE_MINIMIZE' }
  | { type: 'PIN'; payload: PinPosition }
  | { type: 'UPDATE_Z_INDEX'; payload: number }
  | {
      type: 'SNAP_TO_EDGE';
      payload: { position: WindowPosition; size: WindowSize };
    }
  | {
      type: 'SET_PREVIOUS_STATE';
      payload: { size: WindowSize; position: WindowPosition };
    };

const snapToGrid = (value: number, grid: number): number => {
  return Math.round(value / grid) * grid;
};

const isEqual = (obj1: unknown, obj2: unknown): boolean => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

const checkSnapToEdge = (
  position: WindowPosition,
  size: WindowSize,
  containerBounds: DOMRect,
  threshold = 20
): { position: WindowPosition; size: WindowSize } | null => {
  const result = {
    position: { ...position },
    size: { ...size, unit: size.unit },
  };
  let shouldSnap = false;

  // Snap to left edge
  if (position.x < threshold) {
    result.position.x = 0;
    shouldSnap = true;
  }
  // Snap to right edge
  else if (containerBounds.width - (position.x + size.width) < threshold) {
    result.position.x = containerBounds.width - size.width;
    shouldSnap = true;
  }

  // Snap to top edge
  if (position.y < threshold) {
    result.position.y = 0;
    shouldSnap = true;
  }
  // Snap to bottom edge
  else if (containerBounds.height - (position.y + size.height) < threshold) {
    result.position.y = containerBounds.height - size.height;
    shouldSnap = true;
  }

  return shouldSnap ? result : null;
};

const isWindowOutsideBounds = (
  position: WindowPosition,
  size: WindowSize,
  containerBounds: DOMRect
): boolean => {
  const windowRight = position.x + size.width;
  const windowBottom = position.y + size.height;

  // Check if window is completely outside the container bounds
  return (
    windowRight < 0 || // completely to the left
    position.x > containerBounds.width || // completely to the right
    windowBottom < 0 || // completely above
    position.y > containerBounds.height // completely below
  );
};

const windowReducer = produce((draft: WindowState, action: WindowAction) => {
  switch (action.type) {
    case 'MOVE':
      draft.position = action.payload;
      break;
    case 'START_DRAG':
      draft.isDragging = true;
      break;
    case 'END_DRAG':
      draft.isDragging = false;
      break;
    case 'RESIZE':
      if (action.payload.width !== undefined) draft.size.width = action.payload.width;
      if (action.payload.height !== undefined) draft.size.height = action.payload.height;
      if (action.payload.unit) draft.size.unit = action.payload.unit;
      break;
    case 'START_RESIZE':
      draft.isResizing = true;
      break;
    case 'END_RESIZE':
      draft.isResizing = false;
      break;
    case 'TOGGLE_MINIMIZE':
      if (draft.isMinimized) {
        if (draft.previousSize) {
          draft.size = draft.previousSize;
          draft.previousSize = undefined;
        }
      } else {
        draft.previousSize = { ...draft.size };
        draft.size = { ...draft.size, height: 42 };
      }
      draft.isMinimized = !draft.isMinimized;
      break;
    case 'PIN':
      draft.pinPosition = action.payload;
      // Clear previous state when unpinning
      if (!action.payload) {
        draft.previousSize = undefined;
        draft.previousPosition = undefined;
      }
      break;
    case 'UPDATE_Z_INDEX':
      draft.zIndex = action.payload;
      break;
    case 'SNAP_TO_EDGE':
      draft.position = action.payload.position;
      draft.size = action.payload.size;
      break;
    case 'SET_PREVIOUS_STATE':
      draft.previousSize = action.payload.size;
      draft.previousPosition = action.payload.position;
      break;
  }
});

const DEFAULT_CONSTRAINTS: WindowConstraints = {
  minWidth: 200,
  minHeight: 200,
  maxWidth: Infinity,
  maxHeight: Infinity,
  snapToGrid: 0,
};

// Helper function to parse size input
const parseSizeInput = (size: WindowSizeInput): WindowSize => {
  const parseValue = (value: string | number): { value: number; unit: 'px' | '%' } => {
    if (typeof value === 'number') {
      return { value, unit: 'px' };
    }
    const match = value.match(/^(\d+(?:\.\d+)?)(px|%)?$/);
    if (!match) {
      throw new Error(`Invalid size value: ${value}`);
    }
    return {
      value: parseFloat(match[1]),
      unit: (match[2] || 'px') as 'px' | '%',
    };
  };

  const widthResult = parseValue(size.width);
  const heightResult = parseValue(size.height);

  // If units are mixed, prefer pixels
  const unit = size.unit || (widthResult.unit === heightResult.unit ? widthResult.unit : 'px');

  return {
    width: widthResult.value,
    height: heightResult.value,
    unit,
  };
};

// Helper function to convert WindowSize to legacy string format
const toLegacySize = (size: WindowSize): LegacySize => ({
  width: size.unit === '%' ? `${size.width}%` : `${size.width}px`,
  height: size.unit === '%' ? `${size.height}%` : `${size.height}px`,
});

export const DraggableWindow: React.FC<DraggableWindowProps> = ({
  title,
  children,
  initialPosition = { x: 0, y: 0 },
  initialSize = { width: 300, height: 200, unit: 'px' },
  // Legacy props
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  dragEnabled,
  onPositionChange,
  onSizeChange,
  // New props
  constraints: userConstraints = {},
  features: userFeatures = {},
  zIndex = 1,
  darkMode = false,
  className,
  style,
  sx,
  headerControls,
  onStateChange,
  footer,
}) => {
  // Add base z-index state
  const [baseZIndex] = useState(zIndex);
  const [isActive, setIsActive] = useState(false);

  // Static z-index values
  const Z_INDEX = {
    BASE: baseZIndex,
    DRAGGING: 1000,
    ACTIVE: 2000,
  } as const;

  // Merge legacy constraints with new constraints
  const constraints = useMemo(
    () => ({
      ...DEFAULT_CONSTRAINTS,
      ...(minWidth !== undefined && { minWidth }),
      ...(minHeight !== undefined && { minHeight }),
      ...(maxWidth !== undefined && { maxWidth }),
      ...(maxHeight !== undefined && { maxHeight }),
      ...userConstraints,
    }),
    [userConstraints, minWidth, minHeight, maxWidth, maxHeight]
  );

  // Merge legacy features with new features
  const features = useMemo(
    () => ({
      minimizable: true,
      pinnable: true,
      resizable: true,
      draggable: dragEnabled ?? true,
      snapToEdges: true,
      ...userFeatures,
    }),
    [dragEnabled, userFeatures]
  );

  const parsedInitialSize = useMemo(() => {
    try {
      return parseSizeInput(initialSize);
    } catch (error) {
      console.warn('Invalid initial size:', error);
      return { width: 300, height: 200, unit: 'px' as const };
    }
  }, [initialSize]);

  const resizeDirection = useRef<ResizeDirection | null>(null);
  const resizeStartPos = useRef<{ x: number; y: number } | null>(null);
  const resizeStartSize = useRef<{ width: number; height: number } | null>(null);
  const resizeStartWindowPos = useRef<{ x: number; y: number } | null>(null);

  const [state, dispatch] = useReducer(windowReducer, {
    position: initialPosition,
    size: parsedInitialSize,
    isMinimized: false,
    pinPosition: null,
    zIndex: Z_INDEX.BASE,
    isDragging: false,
    isResizing: false,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  // Function to handle window activation
  const handleWindowActivate = useCallback(() => {
    if (!isActive) {
      setIsActive(true);
      dispatch({ type: 'UPDATE_Z_INDEX', payload: Z_INDEX.ACTIVE });
    }
  }, [isActive, Z_INDEX.ACTIVE]);

  // Function to handle window deactivation
  const handleWindowDeactivate = useCallback(() => {
    setIsActive(false);
    dispatch({ type: 'UPDATE_Z_INDEX', payload: Z_INDEX.BASE });
  }, [Z_INDEX.BASE]);

  // Add effect to handle window focus/blur
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!element.contains(event.target as Node)) {
        handleWindowDeactivate();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleWindowDeactivate]);

  useEffect(() => {
    if (
      initialPosition.x === 0 &&
      initialPosition.y === 0 &&
      initialPosition &&
      !isEqual(state.position, initialPosition) &&
      !state.isResizing &&
      !state.isDragging &&
      state.pinPosition == null
    ) {
      dispatch({ type: 'MOVE', payload: initialPosition });
    }
  }, [initialPosition, state.position, state.isResizing, state.isDragging, state.pinPosition]);

  // Follow initialSize changes (content-driven defaults, e.g. the Stats chart
  // reporting its measured minimum) until the user takes control of sizing via
  // a manual resize, pin, or minimize -- after that their size wins.
  const userAdjustedSizeRef = useRef(false);
  useEffect(() => {
    if (userAdjustedSizeRef.current || state.isResizing || state.isDragging) {
      return;
    }
    try {
      const parsedSize = parseSizeInput(initialSize);
      if (!isEqual(state.size, parsedSize)) {
        dispatch({ type: 'RESIZE', payload: parsedSize });
      }
    } catch (error) {
      console.warn('Invalid initial size:', error);
    }
  }, [initialSize, state.size, state.isResizing, state.isDragging]);

  // Add resize observer to handle parent container resizing
  useEffect(() => {
    const element = containerRef.current;
    const parent = element?.parentElement;
    if (!element || !parent) return;

    const handleParentResize = () => {
      const parentBounds = parent.getBoundingClientRect();
      if (!parentBounds) return;

      let newSize = { ...state.size };
      let newPos = { ...state.position };
      let needsUpdate = false;

      // Check if window is outside bounds and not pinned
      if (!state.pinPosition && isWindowOutsideBounds(state.position, state.size, parentBounds)) {
        // Reset to initial position and size
        newPos = initialPosition;
        newSize = parsedInitialSize;
        needsUpdate = true;
      }

      // Handle pinned windows
      if (state.pinPosition) {
        switch (state.pinPosition) {
          case 'left':
            newSize.height = parentBounds.height;
            needsUpdate = true;
            break;
          case 'right':
            newSize.height = parentBounds.height;
            newPos.x = parentBounds.width - state.size.width;
            needsUpdate = true;
            break;
          case 'top':
            newSize.width = parentBounds.width;
            needsUpdate = true;
            break;
          case 'bottom':
            newSize.width = parentBounds.width;
            newPos.y = parentBounds.height - state.size.height;
            needsUpdate = true;
            break;
        }
      }

      if (needsUpdate) {
        if (!isEqual(newSize, state.size)) {
          dispatch({ type: 'RESIZE', payload: newSize });
        }
        if (!isEqual(newPos, state.position)) {
          dispatch({ type: 'MOVE', payload: newPos });
        }
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      // Add a small delay to let other windows update first
      setTimeout(handleParentResize, 0);
    });

    resizeObserver.observe(parent);

    return () => {
      resizeObserver.disconnect();
    };
  }, [state.size, state.position, state.pinPosition, initialPosition, parsedInitialSize]);

  // Debounced handlers
  const debouncedPositionChange = useMemo(
    () =>
      debounce((newPosition: WindowPosition) => {
        if (onPositionChange) {
          onPositionChange(newPosition);
        }
      }, 16),
    [onPositionChange]
  );

  const debouncedSizeChange = useMemo(
    () =>
      debounce((newSize: LegacySize) => {
        if (onSizeChange) {
          onSizeChange(newSize);
        }
      }, 16),
    [onSizeChange]
  );

  const debouncedStateChange = useMemo(
    () =>
      debounce((newState: WindowState) => {
        if (onStateChange) {
          onStateChange(newState);
        }
      }, 16),
    [onStateChange]
  );

  // Clean up debounced functions
  useEffect(() => {
    return () => {
      debouncedPositionChange.cancel();
      debouncedSizeChange.cancel();
      debouncedStateChange.cancel();
    };
  }, [debouncedPositionChange, debouncedSizeChange, debouncedStateChange]);

  const handleDragStop = useCallback(
    (e: DraggableEvent, data: DraggableData) => {
      if (!features.draggable) return;

      const newPosition = { x: data.x, y: data.y };

      // Check if window is outside bounds and not pinned
      if (!state.pinPosition && containerRef.current) {
        const containerBounds = containerRef.current.parentElement?.getBoundingClientRect();

        if (containerBounds && isWindowOutsideBounds(newPosition, state.size, containerBounds)) {
          // Reset to initial position
          dispatch({ type: 'MOVE', payload: initialPosition });
          dispatch({ type: 'END_DRAG' });
          debouncedPositionChange(initialPosition);
          return;
        }
      }

      if (features.snapToEdges && containerRef.current?.parentElement) {
        const containerBounds = containerRef.current.parentElement.getBoundingClientRect();
        const snappedPosition = checkSnapToEdge(newPosition, state.size, containerBounds);

        if (snappedPosition) {
          dispatch({ type: 'SNAP_TO_EDGE', payload: snappedPosition });
          debouncedPositionChange(snappedPosition.position);
          return;
        }
      }

      if (constraints.snapToGrid > 0) {
        newPosition.x = snapToGrid(newPosition.x, constraints.snapToGrid);
        newPosition.y = snapToGrid(newPosition.y, constraints.snapToGrid);
      }

      dispatch({ type: 'MOVE', payload: newPosition });
      dispatch({ type: 'END_DRAG' });
      debouncedPositionChange(newPosition);
    },
    [features, constraints, state.size, state.pinPosition, initialPosition, debouncedPositionChange]
  );

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, direction: ResizeDirection) => {
      if (!features.resizable) return;
      e.preventDefault();
      e.stopPropagation();

      const handleMouseMove = (e: MouseEvent) => {
        if (
          !resizeStartPos.current ||
          !resizeStartSize.current ||
          !resizeDirection.current ||
          !resizeStartWindowPos.current
        )
          return;

        const deltaX = e.clientX - resizeStartPos.current.x;
        const deltaY = e.clientY - resizeStartPos.current.y;
        const dir = resizeDirection.current;

        let newWidth = resizeStartSize.current.width;
        let newHeight = resizeStartSize.current.height;
        let newX = resizeStartWindowPos.current.x;
        let newY = resizeStartWindowPos.current.y;

        // Handle width changes
        if (dir.includes('e')) {
          newWidth = Math.max(
            constraints.minWidth,
            Math.min(constraints.maxWidth, resizeStartSize.current.width + deltaX)
          );
        } else if (dir.includes('w')) {
          const possibleWidth = resizeStartSize.current.width - deltaX;
          if (possibleWidth >= constraints.minWidth && possibleWidth <= constraints.maxWidth) {
            newWidth = possibleWidth;
            newX = resizeStartWindowPos.current.x + deltaX;
          }
        }

        // Handle height changes
        if (dir.includes('s')) {
          newHeight = Math.max(
            constraints.minHeight,
            Math.min(constraints.maxHeight, resizeStartSize.current.height + deltaY)
          );
        } else if (dir.includes('n')) {
          const possibleHeight = resizeStartSize.current.height - deltaY;
          if (possibleHeight >= constraints.minHeight && possibleHeight <= constraints.maxHeight) {
            newHeight = possibleHeight;
            newY = resizeStartWindowPos.current.y + deltaY;
          }
        }

        // Apply grid snapping if enabled
        if (constraints.snapToGrid > 0) {
          newWidth = snapToGrid(newWidth, constraints.snapToGrid);
          newHeight = snapToGrid(newHeight, constraints.snapToGrid);
          newX = snapToGrid(newX, constraints.snapToGrid);
          newY = snapToGrid(newY, constraints.snapToGrid);
        }

        // Update size with debounce
        const newSize = {
          width: newWidth,
          height: newHeight,
          unit: state.size.unit,
        };
        dispatch({ type: 'RESIZE', payload: newSize });
        debouncedSizeChange(toLegacySize(newSize));

        // Update position with debounce
        if (newX !== state.position.x || newY !== state.position.y) {
          const newPosition = { x: newX, y: newY };
          dispatch({ type: 'MOVE', payload: newPosition });
          debouncedPositionChange(newPosition);
        }
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        resizeDirection.current = null;
        resizeStartPos.current = null;
        resizeStartSize.current = null;
        resizeStartWindowPos.current = null;
        dispatch({ type: 'END_RESIZE' });
      };

      userAdjustedSizeRef.current = true;
      resizeDirection.current = direction;
      resizeStartPos.current = { x: e.clientX, y: e.clientY };
      resizeStartSize.current = {
        width: state.size.width,
        height: state.size.height,
      };
      resizeStartWindowPos.current = { ...state.position };

      dispatch({ type: 'START_RESIZE' });
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [
      features.resizable,
      state.size,
      state.position,
      debouncedPositionChange,
      debouncedSizeChange,
      constraints.maxHeight,
      constraints.maxWidth,
      constraints.minHeight,
      constraints.minWidth,
      constraints.snapToGrid,
    ]
  );

  const toggleMinimize = useCallback(() => {
    if (!features.minimizable) return;
    userAdjustedSizeRef.current = true;
    dispatch({ type: 'TOGGLE_MINIMIZE' });
  }, [features.minimizable]);

  const handlePinClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!features.pinnable) return;
      setAnchorEl(event.currentTarget);
    },
    [features.pinnable]
  );

  const handlePinClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handlePinSelect = useCallback(
    (newPosition: PinPosition) => {
      handlePinClose();
      userAdjustedSizeRef.current = true;

      if (!containerRef.current?.parentElement) {
        return;
      }

      const parentBounds = containerRef.current.parentElement.getBoundingClientRect();
      let newSize: WindowSize;
      let newPos: WindowPosition;

      // Store current size and position before pinning
      if (!state.pinPosition && newPosition) {
        dispatch({
          type: 'SET_PREVIOUS_STATE',
          payload: {
            size: { ...state.size },
            position: { ...state.position },
          },
        });
      }

      switch (newPosition) {
        case 'left':
        case 'right': {
          const width = Math.min(400, parentBounds.width * 0.3); // 30% of parent width or 400px
          newSize = {
            width,
            height: parentBounds.height,
            unit: 'px',
          };
          newPos = {
            x: newPosition === 'left' ? 0 : parentBounds.width - width,
            y: 0,
          };
          break;
        }
        case 'top':
        case 'bottom': {
          const height = Math.min(300, parentBounds.height * 0.3); // 30% of parent height or 300px
          newSize = {
            width: parentBounds.width,
            height,
            unit: 'px',
          };
          newPos = {
            x: 0,
            y: newPosition === 'top' ? 0 : parentBounds.height - height,
          };
          break;
        }
        default: {
          // Unpin - restore previous size and position if available
          if (state.previousSize && state.previousPosition) {
            newSize = state.previousSize;
            newPos = state.previousPosition;
          } else {
            // Default fallback if no previous state
            newSize = {
              width: 400,
              height: 300,
              unit: 'px',
            };
            newPos = { x: 20, y: 20 };
          }
        }
      }

      dispatch({ type: 'PIN', payload: newPosition });
      dispatch({ type: 'RESIZE', payload: newSize });
      dispatch({ type: 'MOVE', payload: newPos });
    },
    [
      state.size,
      state.position,
      state.pinPosition,
      state.previousSize,
      state.previousPosition,
      handlePinClose,
    ]
  );

  // Update state changes with debounce
  useEffect(() => {
    debouncedStateChange(state);
  }, [state, debouncedStateChange]);

  return (
    <>
      <Draggable
        nodeRef={containerRef as React.RefObject<HTMLElement>}
        handle=".drag-handle"
        position={state.position}
        onStop={handleDragStop}
        onStart={handleWindowActivate}
        bounds="parent"
        disabled={!features.draggable || state.pinPosition !== null}
      >
        <Box
          ref={containerRef}
          className={className}
          onMouseDown={handleWindowActivate}
          sx={{
            width: state.size.unit === '%' ? `${state.size.width}%` : `${state.size.width}px`,
            height: state.isMinimized
              ? 40
              : state.size.unit === '%'
                ? `${state.size.height}%`
                : `${state.size.height}px`,
            minWidth: state.isMinimized ? 0 : minWidth,
            minHeight: state.isMinimized ? 0 : minHeight,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: darkMode ? 'rgba(0, 62, 103, 0.6)' : 'rgba(255, 255, 255, 0.95)',
            borderRadius: '4px',
            backdropFilter: darkMode ? 'blur(8px)' : undefined,
            boxShadow: darkMode ? undefined : 3,
            overflow: 'hidden',
            position: 'absolute',
            pointerEvents: features.draggable ? 'auto' : 'none',
            zIndex: state.zIndex,
            outline: isActive ? '2px solid rgba(0, 120, 212, 0.2)' : 'none',
            transition: 'outline 0.1s ease-in-out',
            '&.react-draggable-dragging': {
              zIndex: Z_INDEX.DRAGGING,
              opacity: 0.8,
              cursor: 'move',
            },
            ...style,
            ...sx,
          }}
        >
          <Box
            className="drag-handle"
            sx={{
              cursor: features.draggable && !state.pinPosition ? 'move' : 'default',
              pointerEvents: 'auto',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              p: 0.5,
              borderBottom: `1px solid ${
                darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.12)'
              }`,
              backgroundColor: darkMode ? undefined : 'rgba(0, 0, 0, 0.02)',
            }}
          >
            <DragIndicatorIcon
              sx={{
                color: darkMode ? 'white' : 'text.secondary',
                opacity: 0.7,
              }}
            />
            <Typography
              variant="subtitle1"
              sx={{
                color: darkMode ? 'white' : 'text.primary',
                opacity: darkMode ? 0.9 : 1,
                fontWeight: 500,
                flex: 1,
                textAlign: 'center',
                mx: 1,
              }}
            >
              {title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {headerControls}
              {features.pinnable && (
                <IconButton
                  onClick={handlePinClick}
                  size="small"
                  sx={{
                    color: darkMode ? 'white' : 'text.secondary',
                    opacity: state.pinPosition ? 0.9 : 0.7,
                    transform: state.pinPosition ? 'rotate(45deg)' : 'none',
                    transition: 'transform 0.2s',
                    mr: 0.5,
                  }}
                >
                  <PushPinIcon fontSize="small" />
                </IconButton>
              )}
              {features.minimizable && (
                <IconButton
                  onClick={toggleMinimize}
                  size="small"
                  sx={{
                    color: darkMode ? 'white' : 'text.secondary',
                    opacity: 0.7,
                  }}
                >
                  {state.isMinimized ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              )}
            </Box>
          </Box>

          {!state.isMinimized && (
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ flex: 1, overflow: 'auto' }}>{children}</Box>
              {footer && (
                <Box
                  sx={{
                    mt: 'auto',
                    pt: 1,
                    borderTop: theme =>
                      `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`,
                  }}
                >
                  {footer}
                </Box>
              )}
            </Box>
          )}

          {features.resizable && !state.isMinimized && (
            <>
              {/* Edge handles */}
              {(!state.pinPosition || state.pinPosition === 'bottom') && (
                <Box
                  onMouseDown={e => handleResizeStart(e, 'n')}
                  sx={{
                    position: 'absolute',
                    top: '-4px',
                    left: '16px',
                    right: '16px',
                    height: '8px',
                    cursor: 'ns-resize',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    },
                  }}
                />
              )}
              {(!state.pinPosition || state.pinPosition === 'top') && (
                <Box
                  onMouseDown={e => handleResizeStart(e, 's')}
                  sx={{
                    position: 'absolute',
                    bottom: '-4px',
                    left: '16px',
                    right: '16px',
                    height: '8px',
                    cursor: 'ns-resize',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    },
                  }}
                />
              )}
              {(!state.pinPosition || state.pinPosition === 'left') && (
                <Box
                  onMouseDown={e => handleResizeStart(e, 'e')}
                  sx={{
                    position: 'absolute',
                    right: '-4px',
                    top: '16px',
                    bottom: '16px',
                    width: '8px',
                    cursor: 'ew-resize',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    },
                  }}
                />
              )}
              {(!state.pinPosition || state.pinPosition === 'right') && (
                <Box
                  onMouseDown={e => handleResizeStart(e, 'w')}
                  sx={{
                    position: 'absolute',
                    left: '-4px',
                    top: '16px',
                    bottom: '16px',
                    width: '8px',
                    cursor: 'ew-resize',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    },
                  }}
                />
              )}

              {/* Corner handles */}
              {(!state.pinPosition ||
                state.pinPosition === 'right' ||
                state.pinPosition === 'bottom') && (
                <Box
                  onMouseDown={e => handleResizeStart(e, 'nw')}
                  sx={{
                    position: 'absolute',
                    top: '-4px',
                    left: '-4px',
                    width: '16px',
                    height: '16px',
                    cursor: 'nw-resize',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    },
                  }}
                />
              )}
              {(!state.pinPosition ||
                state.pinPosition === 'left' ||
                state.pinPosition === 'bottom') && (
                <Box
                  onMouseDown={e => handleResizeStart(e, 'ne')}
                  sx={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    width: '16px',
                    height: '16px',
                    cursor: 'ne-resize',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    },
                  }}
                />
              )}
              {(!state.pinPosition ||
                state.pinPosition === 'right' ||
                state.pinPosition === 'top') && (
                <Box
                  onMouseDown={e => handleResizeStart(e, 'sw')}
                  sx={{
                    position: 'absolute',
                    bottom: '-4px',
                    left: '-4px',
                    width: '16px',
                    height: '16px',
                    cursor: 'sw-resize',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    },
                  }}
                />
              )}
              {(!state.pinPosition ||
                state.pinPosition === 'left' ||
                state.pinPosition === 'top') && (
                <Box
                  onMouseDown={e => handleResizeStart(e, 'se')}
                  sx={{
                    position: 'absolute',
                    bottom: '-4px',
                    right: '-4px',
                    width: '16px',
                    height: '16px',
                    cursor: 'se-resize',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    },
                  }}
                />
              )}
            </>
          )}
        </Box>
      </Draggable>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handlePinClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          zIndex: Z_INDEX.ACTIVE + 100,
        }}
      >
        <MenuItem onClick={() => handlePinSelect('left')} selected={state.pinPosition === 'left'}>
          Pin to Left
        </MenuItem>
        <MenuItem onClick={() => handlePinSelect('right')} selected={state.pinPosition === 'right'}>
          Pin to Right
        </MenuItem>
        <MenuItem onClick={() => handlePinSelect('top')} selected={state.pinPosition === 'top'}>
          Pin to Top
        </MenuItem>
        <MenuItem
          onClick={() => handlePinSelect('bottom')}
          selected={state.pinPosition === 'bottom'}
        >
          Pin to Bottom
        </MenuItem>
        {state.pinPosition && <MenuItem onClick={() => handlePinSelect(null)}>Unpin</MenuItem>}
      </Menu>
    </>
  );
};
